import { useCallback, useEffect, useRef, useState } from "react";
import { AdminUserBase } from "@preveil-api";
import { Account, collectionApi, ADMIN_USER_PAGE_SIZE, PendingAgStatusByUser, PendingAgStatusUser, PostUsersOrgsByEntityIdListPendingAgStatusApiResponse } from "src/common";

/* Description: This is a custom hook for paging through an org's lightweight user list, optionally fetching
   pending-approval-group status once paging completes. */
export function useLightweightUsers(
  current_account: Account,
  entity_id: string,
  include_pending_ag_status: boolean = false,
) {
  const [data, setData] = useState<AdminUserBase[]>();
  const [pending_ag_status_by_user, setPendingAgStatusByUser] = useState<PendingAgStatusByUser>();
  const [error, setError] = useState<unknown>();
  const generation_ref = useRef<number>(0);
  const account_ids = Account.getAccountIdentifiers(current_account);
  const [fetchUsers] = collectionApi.endpoints.getUsersOrgsByEntityIdUsers.useLazyQuery();
  const [postPendingAgStatus] = collectionApi.endpoints.postUsersOrgsByEntityIdListPendingAgStatus.useMutation();

  // Description: Stop any in-flight background load when the component unmounts.
  useEffect(() => {
    return () => {
      generation_ref.current += 1;
    };
  }, []);

  // Description: The backend returns the raw account_type column, which defaults to "full" until a user claims
  // their account. Corrects it to null (account_types.no_user) for anyone who hasn't claimed yet.
  function correctAccountType(user: AdminUserBase): AdminUserBase {
    return user.claimed_at !== null ? user : { ...user, account_type: null };
  }

  // Description: Fetches pending-approval-group status for the given users via the list_pending_ag_status
  // endpoint. Only returns users with a pending change.
  function loadPendingAgStatus(users: AdminUserBase[], generation: number) {
    if (users.length === 0) {
      setPendingAgStatusByUser({});
      return;
    }
    postPendingAgStatus({
      account_ids,
      body: {
        entity_id,
        users: users.map((user: AdminUserBase) => ({ canonical_user_id: user.canonical_user_id.toLowerCase(), account_version: user.account_version })),
      },
    })
      .unwrap()
      .then((response: PostUsersOrgsByEntityIdListPendingAgStatusApiResponse) => {
        if (generation_ref.current !== generation) return;
        setPendingAgStatusByUser(
          response.users.reduce<PendingAgStatusByUser>((map: PendingAgStatusByUser, user: PendingAgStatusUser & { pending_ag_id: string }) => {
            map[user.canonical_user_id.toLowerCase()] = { pending_recovery_group_id: user.pending_ag_id, recovery_group_pending: true };
            return map;
          }, {}),
        );
      })
      .catch((error: unknown) => {
        if (generation_ref.current !== generation) return;
        setError(error);
      });
  }

  // Description: Pages through the org's lightweight user list until a short page arrives, accumulating every
  // page into one list, then triggers the pending-AG-status fetch for that list if requested.
  async function loadLightweightUsers(generation: number) {
    let offset = 0;
    let raw_count = 0;
    let accumulated_users: AdminUserBase[] = [];
    while (true) {
      if (generation_ref.current !== generation) return;
      try {
        const result = await fetchUsers({
          account_ids,
          body: { entity_id, limit: ADMIN_USER_PAGE_SIZE, offset, include_total: true },
        }).unwrap();
        if (generation_ref.current !== generation) return;
        const raw_users = result.users ?? [];
        raw_count += raw_users.length;
        accumulated_users = [...accumulated_users, ...raw_users.filter((user: AdminUserBase) => !user.deleted).map(correctAccountType)];
        const total_reached = result.total_count !== null && raw_count >= result.total_count;
        if (raw_users.length < ADMIN_USER_PAGE_SIZE || total_reached) break;
        offset += ADMIN_USER_PAGE_SIZE;
      } catch (error: unknown) {
        if (generation_ref.current !== generation) return;
        setError(error);
        return;
      }
    }
    setData(accumulated_users);
    include_pending_ag_status && loadPendingAgStatus(accumulated_users, generation);
  }

  /* Description: Fetches the lightweight user list, cancelling any load already in flight. */
  const refresh = useCallback(() => {
    const generation = ++generation_ref.current;
    setData(undefined);
    setPendingAgStatusByUser(undefined);
    setError(undefined);
    loadLightweightUsers(generation);
  }, [entity_id, include_pending_ag_status, current_account]);

  return { data, pending_ag_status_by_user, error, refresh };
}
